'use client'

import { useEffect, useMemo, useState, useCallback, ComponentType } from "react"
import { useSearchParams, usePathname, useRouter } from "next/navigation"
import { Pagination as PaginationMain, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import Input from "./Input"
import Skeleton from "./Skeleton"
import Select from "./Select"
import { Search } from "lucide-react"

type FilterOption = {
  id: string
  fields: string[]
  options: { label: string; value: string }[]
  placeholder?: string
}

interface PaginationProps<T> {
  items: T[]
  component: any
  limit?: number
  empty: ComponentType
  searchFields: string[]
  showSearch?: boolean
  searchPlaceholder?: string
  filterOptions?: FilterOption[]
  containerClassName?: string
}

export default function Pagination<T>({items: itemList, component, limit = 10, empty, searchFields, showSearch = true, searchPlaceholder = "Энд хайна уу...", filterOptions = [], containerClassName = "" }: PaginationProps<T>) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState<string>(searchParams.get('search') || '')
  const page = useMemo(() => parseInt(searchParams.get('page') || "1"), [searchParams])
  const totalPages = useMemo(() => Math.ceil(items.length / limit), [items, limit])
  const currentItems = useMemo(() => items.slice((page - 1) * limit, page * limit), [items, page, limit])
  const Component = useMemo(() => component, [component])
  const Empty = useMemo(() => empty, [empty])

  useEffect(() => {
    let filtered = itemList
    if (search) {
      if (search !== searchParams.get('search')) searchQuery()
      filtered = filtered.filter(item =>
        searchFields.some(field =>
          String((item as any)[field]).toLowerCase().includes(search.toLowerCase())
        )
      )
    } else if (!search) {
      if (search !== searchParams.get('search')) deleteQueryString('search')
    }

    if (filterOptions && filterOptions.length > 0) {
      filterOptions.forEach(filter => {
        const value = searchParams.get(filter.id)
        if (value) {
          filtered = filtered.filter(item =>
            filter.fields.some(field => String((item as any)[field]) === value)
          )
        } else {
          deleteQueryString(filter.id)
        }
      })
    }

    setItems(filtered)
    setLoading(false)
     
  }, [search, filterOptions, searchParams, itemList])

  const createQueryString = useCallback((name: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    params.set(name, value)
    return params.toString()
  }, [searchParams])

  const deleteQueryString = useCallback((name: string) => {
    const params = new URLSearchParams(searchParams)
    params.delete(name)
    return params.toString()
  }, [searchParams])

  const searchQuery = () => {
    router.replace(pathname + '?' + createQueryString('search', search))
  }
  const goToPage = (page: number) => {
    router.replace(pathname + '?' + createQueryString('page', String(page)), { scroll: false })
  }

  if (!itemList) return null

  return (
    <div>
      <div className="flex justify-between items-center">
        {showSearch && (
          <div>
            <Input value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)} placeholder={searchPlaceholder} leftIcon={Search} />
          </div>
        )}
        {(filterOptions && filterOptions.length > 0) && (
          <div>
            {filterOptions.map((filter, index) => (
              <Select
                key={index}
                items={filter.options}
                placeholder={filter.placeholder}
                onChange={(value: string) => router.replace(pathname + '?' + createQueryString(filter.id, value))}
              />
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <Skeleton className="h-32 w-full mt-8" />
      ) : (
        <>
          {(currentItems.length > 0 && items.length > 0) ? (
            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-8 ${containerClassName}`}>
              {currentItems.map((item, index) => (
                <Component item={item} key={index} />
              ))}
            </div>
          ) : (
            <Empty />
          )}
          {totalPages > 1 && (
            <>
              <div className="mt-8 hidden sm:block">
                <PaginationMain>
                  <PaginationContent>
                    {page !== 1 && (
                      <PaginationItem>
                        <PaginationPrevious onClick={() => goToPage(page - 1)} />
                      </PaginationItem>
                    )}
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const pageNumber = index + 1
                      const startPage = Math.max(page - 2, 1)
                      const endPage = Math.min(page + 2, totalPages)
                      if (pageNumber < startPage || pageNumber > endPage) return null
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink isActive={page === pageNumber} onClick={() => goToPage(pageNumber)}>
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    })}
                    {page !== totalPages && (
                      <PaginationItem>
                        <PaginationNext onClick={() => goToPage(page + 1)} />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </PaginationMain>
                <div className="text-xs text-center text-muted-foreground mt-4">
                  Нийт <span className="font-semibold">{totalPages}</span> хуудастай
                </div>
              </div>
              <div className="mt-8 sm:hidden">
                <PaginationMain>
                  <PaginationContent>
                    {page !== 1 && (
                      <PaginationItem>
                        <PaginationPrevious onClick={() => goToPage(page - 1)} />
                      </PaginationItem>
                    )}
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const pageNumber = index + 1
                      const startPage = Math.max(page - 1, 1)
                      const endPage = Math.min(page + 1, totalPages)
                      if (pageNumber < startPage || pageNumber > endPage) return null
                      return (
                        <PaginationItem key={pageNumber}>
                          <PaginationLink isActive={page === pageNumber} onClick={() => goToPage(pageNumber)}>
                            {pageNumber}
                          </PaginationLink>
                        </PaginationItem>
                      )
                    })}
                    {page !== totalPages && (
                      <PaginationItem>
                        <PaginationNext onClick={() => goToPage(page + 1)} />
                      </PaginationItem>
                    )}
                  </PaginationContent>
                </PaginationMain>
                <div className="text-xs text-center text-muted-foreground mt-4">
                  Нийт <span className="font-semibold">{totalPages}</span> хуудастай
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}